import { A4_PAGE_HEIGHT } from '@/constants';
import {
    PADDING_Y,
    BUFFER,
    SECTION_GAP,
    DEFAULT_HEADER_HEIGHT,
    DEFAULT_FOOTER_HEIGHT,
    DEFAULT_PATIENT_INFO_HEIGHT,
    MIN_PAGE1_CONTENT_HEIGHT,
    REPORT_FOOTER_MEASUREMENT_ID,
    REPORT_HEADER_MEASUREMENT_ID,
    REPORT_PATIENT_INFO_MEASUREMENT_ID,
} from './constants';
import { RenderableSection } from './types';
import type { ReportFootnote } from '../footnotes';

export class PageManager {
    pages: RenderableSection[][] = [];
    pageFootnotes: ReportFootnote[][] = [];
    pageMap: Record<string, number> = {};
    currentPageIndex = 0;
    currentHeightUsed = 0;

    // Calculated available heights
    private availableHeightPerPage: number;
    private availableHeightPage1: number;
    private firstPageTooTight: boolean;

    constructor(measurements: Record<string, number>) {
        const headerHeight = measurements[REPORT_HEADER_MEASUREMENT_ID] || DEFAULT_HEADER_HEIGHT;
        const footerHeight = measurements[REPORT_FOOTER_MEASUREMENT_ID] || DEFAULT_FOOTER_HEIGHT;
        const patientInfoHeight =
            measurements[REPORT_PATIENT_INFO_MEASUREMENT_ID] || DEFAULT_PATIENT_INFO_HEIGHT;

        this.availableHeightPerPage =
            A4_PAGE_HEIGHT - footerHeight - PADDING_Y - headerHeight - BUFFER;
        this.availableHeightPage1 =
            this.availableHeightPerPage -
            patientInfoHeight -
            (patientInfoHeight > 0 ? SECTION_GAP : 0);
        this.firstPageTooTight = this.availableHeightPage1 < MIN_PAGE1_CONTENT_HEIGHT;

        // Initialize first page
        this.pages.push([]);
        this.pageFootnotes.push([]);
    }

    /**
     * True quando a tabela de identificação (que cresce com a Finalidade)
     * não deixa espaço útil na primeira página. O caller começa o conteúdo na
     * página 2 — sem isso, `process-text` trataria a página 1 como "fresca" e
     * empilharia a primeira seção inteira nela, estourando a folha A4.
     */
    shouldSkipFirstPage() {
        return this.firstPageTooTight;
    }

    startNewPage() {
        this.pages.push([]);
        this.pageFootnotes.push([]);
        this.currentPageIndex++;
        this.currentHeightUsed = 0;
    }

    recordPage(id: string) {
        if (!this.pageMap[id]) {
            this.pageMap[id] = this.currentPageIndex + 1;
        }
    }

    getRemainingSpace() {
        const maxPageHeight =
            this.currentPageIndex === 0 ? this.availableHeightPage1 : this.availableHeightPerPage;
        return maxPageHeight - this.currentHeightUsed;
    }

    addToPage(section: RenderableSection) {
        this.pages[this.currentPageIndex].push(section);
    }

    addFootnotes(footnotes: ReportFootnote[]) {
        if (footnotes.length === 0) return;

        this.pageFootnotes[this.currentPageIndex].push(...footnotes);
    }

    hasFootnotesOnCurrentPage() {
        return this.pageFootnotes[this.currentPageIndex].length > 0;
    }

    addHeight(amount: number) {
        this.currentHeightUsed += amount;
    }

    ensureTrailingSpace(amount: number) {
        if (amount <= this.getRemainingSpace() || this.currentHeightUsed === 0) {
            return;
        }

        this.startNewPage();
    }

    forceNewPageIfFull() {
        if (this.getRemainingSpace() <= 0 && this.currentHeightUsed > 0) {
            this.startNewPage();
        }
    }

    isFreshPage() {
        return this.currentHeightUsed < 50;
    }

    resetRemainingSpace() {
        // Only logically resets concept of remaining space by starting a new page if needed,
        // but this method might be redundant if we use startNewPage.
        // In the original code: remainingSpace = availableHeightPerPage;
        // This happens implicitly when startNewPage is called.
    }

    getCurrentAvailableHeight() {
        return this.currentPageIndex === 0
            ? this.availableHeightPage1
            : this.availableHeightPerPage;
    }
}
